import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { ProtoBase } from '../ProtoBase'
import { SITES } from '../kanjinData'

interface Exhibit {
  group: THREE.Group
  name: string
}

/** ⑫ CG図鑑 — Meshy生成の建造物12棟＋武四郎アバターを回転展示で鑑賞する */
export class Proto12 extends ProtoBase {
  readonly title = '12. CG図鑑'
  readonly desc = 'Meshy生成の寄進地建造物12棟＋武四郎アバターをぐるりと展示（ドラッグで見回し）'

  private exhibits: Exhibit[] = []
  private capIdx = -1
  private capT = 0
  private narratorMoved = false

  protected start(): void {
    this.ctx.setWorld('void')
    this.ctx.view.setPose(new THREE.Vector3(0, 3.4, 15.5), new THREE.Vector3(0, 1.2, 0))
    this.ctx.setCaption('CG図鑑 — 木片のふるさと十二棟と、語り部・武四郎')

    // 床
    const floor = new THREE.Mesh(
      new THREE.CircleGeometry(16, 48),
      new THREE.MeshStandardMaterial({ color: 0x181d26, roughness: 0.95 }),
    )
    floor.rotation.x = -Math.PI / 2
    this.group.add(floor)

    const spot = new THREE.PointLight(0xffe8c0, 60, 40)
    spot.position.set(0, 8, 4)
    this.group.add(spot)

    // 中央: 武四郎（リグから借りて中央台座へ）
    const centerPed = Proto12.makePedestal(1.0)
    this.group.add(centerPed)
    if (this.ctx.narrator.loaded) {
      this.narratorMoved = true
      this.ctx.narrator.root.removeFromParent()
      this.ctx.narrator.root.position.set(0, 0.24, 0)
      this.ctx.narrator.root.rotation.set(0, 0, 0)
      this.ctx.narrator.root.visible = true
      centerPed.add(this.ctx.narrator.root)
      centerPed.add(Proto12.makeLabel('松浦武四郎（語り部）', 2.2))
    }

    // 弧状に12棟（GLBは遅延ロード・失敗時は台座のみ）
    const loader = new GLTFLoader()
    const n = SITES.length
    SITES.forEach((site, i) => {
      // 重なり防止: 半径を11に拡大（隣接間隔≈2.6u > モデル最大2.1u）、ラベルは交互の高さに
      const angle = (-75 + (150 / (n - 1)) * i) * (Math.PI / 180)
      const r = 11
      const ped = Proto12.makePedestal(0.7)
      ped.position.set(Math.sin(angle) * r, 0, -Math.cos(angle) * r + 6.5)
      ped.rotation.y = -angle
      this.group.add(ped)
      ped.add(Proto12.makeLabel(`${site.building}`, i % 2 === 0 ? 2.9 : 3.75, `${site.piece}`))
      const ex: Exhibit = { group: ped, name: `${site.building} — ${site.piece}` }
      this.exhibits.push(ex)
      void loader
        .loadAsync(`./assets/sites3d/${site.no}.glb`)
        .then((gltf) => {
          const model = gltf.scene
          const b = new THREE.Box3().setFromObject(model)
          const size = b.getSize(new THREE.Vector3())
          model.scale.setScalar(2.1 / Math.max(size.x, size.y, size.z, 0.01))
          const b2 = new THREE.Box3().setFromObject(model)
          const c2 = b2.getCenter(new THREE.Vector3())
          model.position.set(-c2.x, 0.24 - b2.min.y, -c2.z)
          ped.add(model)
        })
        .catch(() => void 0)
    })
  }

  update(dt: number): void {
    this.elapsed += dt
    this.capT += dt
    // 展示台がゆっくり自転
    for (const ex of this.exhibits) ex.group.rotation.y += dt * 0.35
    // キャプションを順送り
    if (this.capT > 3.2 && this.exhibits.length > 0) {
      this.capT = 0
      this.capIdx = (this.capIdx + 1) % this.exhibits.length
      const site = SITES[this.capIdx]
      this.ctx.setCaption(`${site.building} — 「${site.piece}」\n${site.note}（${site.era}）`)
    }
  }

  protected onDispose(): void {
    if (this.narratorMoved) {
      this.ctx.narrator.restoreHome(this.ctx.view.rig)
      this.ctx.narrator.root.visible = this.ctx.view.xrActive
    }
  }

  private static makePedestal(radius: number): THREE.Group {
    const g = new THREE.Group()
    const ped = new THREE.Mesh(
      new THREE.CylinderGeometry(radius, radius * 1.15, 0.24, 24),
      new THREE.MeshStandardMaterial({ color: 0x3a3020, roughness: 0.6 }),
    )
    ped.position.y = 0.12
    g.add(ped)
    const rim = new THREE.Mesh(
      new THREE.TorusGeometry(radius * 1.02, 0.02, 8, 40),
      new THREE.MeshBasicMaterial({ color: 0xffd77a, transparent: true, opacity: 0.7, blending: THREE.AdditiveBlending }),
    )
    rim.rotation.x = Math.PI / 2
    rim.position.y = 0.25
    g.add(rim)
    return g
  }

  private static makeLabel(main: string, y: number, sub?: string): THREE.Sprite {
    const c = document.createElement('canvas')
    c.width = 512
    c.height = 140
    const ctx = c.getContext('2d')!
    ctx.fillStyle = 'rgba(10,12,18,0.6)'
    ctx.beginPath()
    ctx.roundRect(4, 4, 504, 132, 12)
    ctx.fill()
    ctx.textAlign = 'center'
    ctx.fillStyle = '#f0e6c8'
    ctx.font = '600 40px "HGP行書体", "HG行書体", "Yu Mincho", serif'
    ctx.fillText(main, 256, sub ? 56 : 84)
    if (sub) {
      ctx.fillStyle = '#ffd77a'
      ctx.font = '500 32px "HGP行書体", "HG行書体", "Yu Mincho", serif'
      ctx.fillText(sub, 256, 108)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }))
    sp.position.y = y
    sp.scale.set(3.4, 0.93, 1)
    return sp
  }
}
